import React from 'react';

import { ButtonLinkType } from '../../graphql-fragments/ButtonLink';
import { NestedEntry, SingleEntry, isNestedEntry } from '../../graphql-fragments/navigationMenu';
import bottomCloud from '../../images/dropdownMenu_cloud_front.webp';
import leftCloud from '../../images/dropdownMenu_cloud_left.webp';
import rightCloud from '../../images/dropdownMenu_cloud_right.webp';
import { BackgroundColorTheme } from '../../templates/Page';
import { isEntryActive, simplifyToLightOrDarkTheme } from '../../utils/projectUtils';
import { getUrlFromVersatileLink } from '../../utils/sanity';
import { clsx } from '../../utils/utils';
import ButtonLink from './ButtonLink';
import * as styles from './DropDownMenu.module.scss';
import MenuEntry from './MenuEntry';
import NestedMenuEntry from './NestedMenuEntry';
import ScrollingItems from './ScrollingItems';
import SmartLink from './SmartLink';

interface DropDownMenuProps {
  entries?: Array<SingleEntry | NestedEntry>;
  legalLinks: Array<ButtonLinkType>;
  className?: string;
  ctaButtons?: Array<ButtonLinkType>;
  theme?: BackgroundColorTheme;
  onEntryClick?: (entry: SingleEntry | NestedEntry, event: React.MouseEvent<HTMLElement>) => void;
  onCtaButtonClick?: React.MouseEventHandler<HTMLElement>;
}

const DropDownMenu = ({
  entries,
  legalLinks,
  ctaButtons,
  className,
  theme,
  onEntryClick,
  onCtaButtonClick,
}: DropDownMenuProps): React.ReactElement => {
  return (
    <div
      className={clsx(styles.container, className)}
      data-theme={simplifyToLightOrDarkTheme(theme)}
    >
      <ScrollingItems className={styles.bottomCloudContainer} animationDuration={200}>
        <img src={bottomCloud} alt="" className={styles.cloud} loading="lazy" width={2168} height={520} />
      </ScrollingItems>
      <ScrollingItems
        className={styles.rightCloudContainer}
        elementsClassName={styles.rightCloudElementsContainer}
        animationDuration={300}
      >
        <img src={rightCloud} alt="" className={styles.cloud} loading="lazy" width={440} height={184} />
      </ScrollingItems>
      <ScrollingItems className={styles.leftCloudContainer} animationDuration={250}>
        <img src={leftCloud} alt="" className={styles.cloud} loading="lazy" width={322} height={134} />
      </ScrollingItems>
      <div className={styles.contentContainer}>
        {entries && (
          <div className={styles.entriesContainer}>
            {entries.map((entry, i: number) => (
              <React.Fragment key={i}>
                {isNestedEntry(entry) ? (
                  <NestedMenuEntry
                    key={i}
                    entry={entry}
                    onClick={event => {
                      onEntryClick && onEntryClick(entry, event);
                    }}
                  />
                ) : (
                  <MenuEntry
                    entry={entry}
                    key={i}
                    onClick={event => {
                      onEntryClick && onEntryClick(entry, event);
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {ctaButtons && ctaButtons.length > 0 && (
          <div className={styles.buttonContainer}>
            {ctaButtons.map((ctaButton, i) => (
              <ButtonLink
                key={i}
                button={ctaButton}
                className={styles.dropdownMenuButton}
                onClick={onCtaButtonClick}
              ></ButtonLink>
            ))}
          </div>
        )}
        <div className={styles.divider}></div>
        <div className={styles.entriesContainer}>
          {legalLinks.map((legalLink, i) => (
            <SmartLink
              className={clsx(styles.legalLink, isEntryActive(legalLink) && styles.active)}
              key={i}
              to={getUrlFromVersatileLink(legalLink)}
            >
              {legalLink.text}
            </SmartLink>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DropDownMenu;
